import React, { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getCamembert } from "../services/LoginService";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const PieChart = ({ labels, values, rawValues }) => {
    // Palette dynamique
    const defaultColors = [
        "#1A2A6C", "#FF6F61", "#FFD700", "#90EE90",
        "#FF69B4", "#8A2BE2", "#00CED1", "#FF8C00"
    ];
    const colorPalette = labels.map((_, i) => defaultColors[i % defaultColors.length]);

    const chartData = {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: colorPalette,
                borderColor: "#fff",
                borderWidth: 2,
            }
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "right",
                labels: {
                    font: { size: 15 },
                    boxWidth: 20,
                    padding: 10,
                    generateLabels: (chart) =>
                        chart.data.labels.map((label, i) => ({
                            text: `${label} (${rawValues[i].toFixed(2)} €)`,
                            fillStyle: chart.data.datasets[0].backgroundColor[i],
                            strokeStyle: chart.data.datasets[0].borderColor[i],
                            index: i,
                        })),
                },
                // Force à rester en colonne
                align: 'start', // aligne les items de la légende verticalement
                maxHeight: 550, // limite la hauteur si nécessaire
            },

            tooltip: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem) => {
                        const val = rawValues[tooltipItem.dataIndex] || 0;
                        const percent = values[tooltipItem.dataIndex].toFixed(1);
                        return `${val.toFixed(2)} € (${percent}%)`;
                    }
                }
            },
            datalabels: {
                color: "#000",
                font: { weight: "bold", size: 12 },
                formatter: (value) => {
                    // n'affiche la valeur que si elle est supérieure à 10
                    return value > 10 ? `${value.toFixed(1)}%` : '';
                },
                anchor: 'end',   // place le label en dehors du secteur
                align: 'center', // aligne le label à l’extérieur
            }

        },
    };

    return <Pie data={chartData} options={options} plugins={[ChartDataLabels]} />;
};

const RepartitionSecteurs = ({ token }) => {
    const [secteursBatiment, setSecteursBatiment] = useState({});
    const [secteursAutres, setSecteursAutres] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            if (!token) return;
            const result = await getCamembert(token);
            if (!result.success) return;

            setSecteursBatiment(result.revenu_batiment || {});
            setSecteursAutres(result.revenu_autres || {});
        };
        fetchData();
    }, [token]);

    const hasBatiment = Object.keys(secteursBatiment).length > 2;
    const hasAutres = Object.keys(secteursAutres).length > 2;

    if (!hasBatiment && !hasAutres) return null;

    const getPercentValues = (revenus) => {
        const total = Object.values(revenus).reduce((a, b) => a + b, 0);
        return Object.values(revenus).map(v => (v / total) * 100);
    };

    return (
        <div className="flex   flex-col items-center gap-6 w-full mt-2 min-h-[350px] ">
            {hasBatiment && (
                <div className="flex-1 lg:w-full w-1/2 bg-white p-4 shadow rounded flex flex-col">
                    <h3 className="text-center font-semibold mb-2"> Chiffre d'affaires dans les secteurs Bâtiment </h3>
                    <div className="flex-1">
                        <PieChart
                            labels={Object.keys(secteursBatiment)}
                            values={getPercentValues(secteursBatiment)}
                            rawValues={Object.values(secteursBatiment)}
                        />
                    </div>
                </div>
            )}

            {hasAutres && (
                <div className="flex-1 bg-white lg:w-full w-1/2 p-4 shadow rounded flex flex-col">
                    <h3 className="text-center font-semibold mb-2"> Chiffre d'affaires dans les autres secteurs</h3>
                    <div className="flex-1">
                        <PieChart
                            labels={Object.keys(secteursAutres)}
                            values={getPercentValues(secteursAutres)}
                            rawValues={Object.values(secteursAutres)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RepartitionSecteurs;
